import styled from 'styled-components';

/**
 * Prose wrapper for HTML produced by the admin editor (CKEditor 5) and legacy board content.
 * Restores list bullets / table borders / headings etc. that GlobalStyle resets,
 * keeps images and embeds inside the column on mobile.
 *
 * Props: $small (14px base for comments), $color
 */
export const ContentBody = styled.div`
  font-size: ${({ $small }) => ($small ? '0.875rem' : '1rem')};
  line-height: 1.7;
  color: ${({ $color, theme }) => theme.colors[$color] || $color || theme.colors.strong};
  word-break: keep-all;
  overflow-wrap: anywhere;
  min-width: 0;

  & > *:first-child { margin-top: 0; }
  & > *:last-child { margin-bottom: 0; }

  p { margin: 0 0 0.6em; }
  h1, h2, h3, h4, h5, h6 { font-weight: 700; line-height: 1.35; margin: 1.4em 0 0.6em; }
  h1 { font-size: 1.75em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  h4 { font-size: 1.1em; }
  h5, h6 { font-size: 1em; }

  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  s, strike { text-decoration: line-through; }
  a { color: ${({ theme }) => theme.colors.primary900}; text-decoration: underline; }

  ul, ol { margin: 0 0 1em; padding-left: 1.6em; }
  ul { list-style: disc; }
  ul ul { list-style: circle; }
  ol { list-style: decimal; }
  ol[type='a'] { list-style: lower-alpha; }
  ol[type='A'] { list-style: upper-alpha; }
  ol[type='i'] { list-style: lower-roman; }
  ol[type='I'] { list-style: upper-roman; }
  li { margin: 0.2em 0; }
  li > p { margin: 0; }

  blockquote {
    margin: 1em 0;
    padding: 0.5em 1.2em;
    border-left: 4px solid ${({ theme }) => theme.colors.primary900};
    background: rgba(23, 23, 23, 0.03);
    color: rgba(23, 23, 23, 0.72);
    font-style: italic;
  }
  blockquote > p:last-child { margin-bottom: 0; }

  hr { border: 0; border-top: 1px solid rgba(23, 23, 23, 0.12); margin: 1.5em 0; }

  pre {
    background: #f4f5f7;
    padding: 1em;
    border-radius: 6px;
    overflow: auto;
    white-space: pre-wrap;
    font-family: Menlo, Consolas, 'Courier New', monospace;
    font-size: 0.875em;
    margin: 0 0 1em;
  }
  code { font-family: Menlo, Consolas, 'Courier New', monospace; background: #f4f5f7; padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.9em; }

  img { max-width: 100%; height: auto; }

  /* CKEditor 5 image figures */
  figure { margin: 1em 0; }
  figure.image {
    display: table;
    clear: both;
    text-align: center;
    margin: 1em auto;
    min-width: 50px;
    max-width: 100%;
  }
  figure.image img { display: block; margin: 0 auto; max-width: 100%; height: auto; }
  figure.image.image_resized { display: block; box-sizing: border-box; max-width: 100%; }
  figure.image.image_resized img { width: 100%; }
  figure.image.image_resized > figcaption { display: block; }
  /* table-caption 은 display:table 인 figure.image 안에서만. 일반 figure 에 쓰면 폭 0 으로 접혀 글자가 세로로 깨짐 */
  figcaption {
    display: block;
    padding: 0.5em;
    font-size: 0.8125em;
    color: rgba(23, 23, 23, 0.55);
    text-align: center;
    word-break: break-word;
  }
  figure.image > figcaption { display: table-caption; caption-side: bottom; }
  .image-inline { display: inline-flex; max-width: 100%; align-items: flex-start; vertical-align: middle; }
  .image-inline img { flex-grow: 1; flex-shrink: 1; max-width: 100%; }
  img.image_resized { height: auto; }
  .image-style-side { float: right; margin-left: 1.5em; max-width: 50%; }
  .image-style-align-left { float: left; margin-right: 1.5em; }
  .image-style-align-right { float: right; margin-left: 1.5em; }
  .image-style-align-center { margin-left: auto; margin-right: auto; }
  .image-style-block-align-left { margin-right: auto; margin-left: 0; }
  .image-style-block-align-right { margin-left: auto; margin-right: 0; }

  /* tables (CKEditor wraps them in <figure class="table">) */
  figure.table { margin: 1em 0; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  figure.table table { width: 100%; }
  table { border-collapse: collapse; border-spacing: 0; max-width: 100%; margin: 0 0 1em; }
  figure.table > table { margin: 0; }
  td, th {
    border: 1px solid rgba(23, 23, 23, 0.18);
    padding: 0.5em 0.75em;
    vertical-align: top;
    min-width: 2em;
  }
  th { background: rgba(23, 23, 23, 0.04); font-weight: 600; }
  table p { margin: 0; }

  /* media embeds (CKEditor previewsInData: true => figure.media > div[data-oembed-url] > iframe) */
  figure.media { margin: 1em 0; clear: both; }
  figure.media > div[data-oembed-url],
  .media-embed {
    position: relative;
    width: 100%;
    height: 0 !important;
    padding-bottom: 56.25% !important;
    overflow: hidden;
  }
  figure.media iframe,
  .media-embed iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    border: 0;
  }
  iframe { max-width: 100%; }
  video { max-width: 100%; height: auto; }

  /* CKEditor font-size presets */
  .text-tiny { font-size: 0.7em; }
  .text-small { font-size: 0.85em; }
  .text-big { font-size: 1.4em; }
  .text-huge { font-size: 1.8em; }

  /* text alignment emitted as inline style still wins; these are for legacy class based alignment */
  .ck-content-align-center, .text-center { text-align: center; }
  .text-right { text-align: right; }

  &::after { content: ''; display: table; clear: both; }

  @media ${({ theme }) => theme.device.mobile} {
    font-size: ${({ $small }) => ($small ? '0.8125rem' : '0.9375rem')};
    .image-style-side,
    .image-style-align-left,
    .image-style-align-right {
      float: none;
      margin-left: auto;
      margin-right: auto;
      max-width: 100%;
    }
    td, th { padding: 0.4em 0.5em; }
  }
`;
